import { useEffect, useRef } from 'react';

type SurveyedEntry = { description: string; cube: number };
type DetectionFrame = { timestamp: number; surveyed?: SurveyedEntry[] };

export const DETECTION_SURVEYED_CHANNEL = 'DETECTION_SURVEYED_ITEMS';
export type SurveyedItemCounts = Record<string, number>;

// Runs in the video popup window. As the video plays, fires each frame's
// surveyed items to the opener (main window) when their timestamp is reached.
// Stops after one full loop.
export function useDetectionAutoSurvey(
  videoContainerRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean
) {
  const doneRef = useRef(false);
  const hasLoopedRef = useRef(false);
  const framesRef = useRef<DetectionFrame[]>([]);
  const lastAppliedIndexRef = useRef(-1);
  const enableTimeRef = useRef(-1); // video.currentTime captured on very first tick
  const rafRef = useRef(0);
  const prevTimeRef = useRef(0);

  // Reset scan state when detection is toggled off
  useEffect(() => {
    if (!enabled) {
      doneRef.current = false;
      hasLoopedRef.current = false;
      lastAppliedIndexRef.current = -1;
      enableTimeRef.current = -1;
      prevTimeRef.current = 0;
    }
  }, [enabled]);

  // Load frame data once detection is enabled
  useEffect(() => {
    if (!enabled) return;
    fetch('/detections.json')
      .then((r) => r.json())
      .then((frames: DetectionFrame[]) => {
        framesRef.current = frames;
      })
      .catch(() => {});
  }, [enabled]);

  // RAF loop: as video plays, fire each frame's surveyed items when its
  // timestamp is reached. Stops after detecting one full loop.
  useEffect(() => {
    if (!enabled) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      return;
    }

    const tick = () => {
      const video = videoContainerRef.current?.querySelector('video');
      if (video && !doneRef.current && !isNaN(video.duration)) {
        const curr = video.currentTime;
        const prev = prevTimeRef.current;

        // Capture the video time on the very first tick so we know where detection
        // was enabled, even if the frames JSON hasn't loaded yet
        if (enableTimeRef.current < 0) {
          enableTimeRef.current = curr;
        }

        // Once frames load, initialize lastAppliedIndex to just before the time
        // detection was enabled (not current time, which may have advanced past items)
        if (
          lastAppliedIndexRef.current === -1 &&
          framesRef.current.length > 0
        ) {
          const frames = framesRef.current;
          const startTime = enableTimeRef.current;
          let i = 0;
          while (i < frames.length && frames[i].timestamp < startTime) i++;
          lastAppliedIndexRef.current = i - 1;
          prevTimeRef.current = curr;
        }

        // Detect loop: time jumped backward (handles any loop-back point, not just 0)
        if (prev > 0 && curr < prev - 1.0) {
          if (hasLoopedRef.current) {
            // Second loop — we've had a full catch-up pass, stop now
            doneRef.current = true;
          } else {
            // First loop — reset to catch up on items missed before detection was enabled
            hasLoopedRef.current = true;
            const frames = framesRef.current;
            let i = 0;
            while (i < frames.length && frames[i].timestamp < curr) i++;
            lastAppliedIndexRef.current = i - 1;
          }
        } else {
          const frames = framesRef.current;
          const target = window.opener ?? window;
          const origin = window.location.origin;

          for (
            let i = lastAppliedIndexRef.current + 1;
            i < frames.length;
            i++
          ) {
            const frame = frames[i];
            if (frame.timestamp > curr) break;

            const surveyed = frame.surveyed ?? [];
            if (surveyed.length > 0) {
              const payload: SurveyedItemCounts = {};
              for (const s of surveyed) {
                if (s.description) {
                  payload[s.description] = (payload[s.description] ?? 0) + 1;
                }
              }
              target.postMessage(
                { type: DETECTION_SURVEYED_CHANNEL, items: payload },
                origin
              );
            }
            lastAppliedIndexRef.current = i;
          }
        }

        prevTimeRef.current = curr;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, [enabled, videoContainerRef]);
}
